import React, { useState, useEffect } from "react";
import { 
  User, 
  CreditCard, 
  Building2, 
  Hash, 
  Save, 
  X,
  ShieldCheck,
  Edit3
} from "lucide-react";
import { toast } from "react-hot-toast";
import { editPayrollEmployeeApi } from "../../../app/api";

const EditPayrollInfoModal = ({ isOpen, onClose, employee, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    account_name: "",
    account_number: "",
    bank_name: "",
    ifsc_code: "",
    pan_number: "",
    salary: ""
  });

  useEffect(() => {
    if (employee) {
      setFormData({
        account_name: employee.account_name || "",
        account_number: employee.account_number || "",
        bank_name: employee.bank_name || "",
        ifsc_code: employee.ifsc_code || "",
        pan_number: employee.pan_number || "",
        salary: employee.salary || ""
      });
    }
  }, [employee]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const res = await editPayrollEmployeeApi(employee.employee_id, formData);
      if (res.success) {
        toast.success("Payroll details updated successfully!");
        onUpdate();
        onClose();
      } else {
        toast.error(res.message || "Failed to update details");
      }
    } catch (error) {
      console.error("Error updating details:", error);
      toast.error("An error occurred while saving");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 animate-in fade-in duration-300">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-md transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="relative bg-[#1B103F] border border-white/10 w-full max-w-xl max-h-[90vh] overflow-y-auto no-scrollbar rounded-[32px] shadow-2xl p-8 space-y-6 scrollbar-hide animate-in zoom-in-95 duration-300">
        
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-6 right-6 p-2 bg-white/5 hover:bg-white/10 text-white rounded-full transition border border-white/10"
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-16 h-16 bg-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-blue-500/30">
            <Edit3 className="text-blue-400" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-white tracking-tight">Edit Payroll Details</h2>
          <p className="text-white/40 text-sm">Update banking and salary info for {employee?.name}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 pt-4">
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputGroup 
              label="Bank Name" 
              name="bank_name" 
              value={formData.bank_name} 
              onChange={handleChange} 
              icon={Building2} 
              placeholder="e.g. HDFC Bank" 
              required
            />
            <InputGroup 
              label="Account Holder Name" 
              name="account_name" 
              value={formData.account_name} 
              onChange={handleChange} 
              icon={User} 
              placeholder="e.g. John Doe" 
              required
            />
            <InputGroup 
              label="Account Number" 
              name="account_number" 
              value={formData.account_number} 
              onChange={handleChange} 
              icon={CreditCard} 
              placeholder="e.g. 501004325678" 
              required
            />
            <InputGroup 
              label="IFSC Code" 
              name="ifsc_code" 
              value={formData.ifsc_code} 
              onChange={handleChange} 
              icon={Hash} 
              placeholder="e.g. HDFC0001234" 
              required
            />
            <InputGroup 
              label="PAN Number" 
              name="pan_number" 
              value={formData.pan_number} 
              onChange={handleChange} 
              icon={ShieldCheck} 
              placeholder="e.g. ABCDE1234F" 
              required
            />
            <div className="md:col-span-2">
              <InputGroup 
                label="Base Salary" 
                name="salary" 
                type="number"
                value={formData.salary} 
                onChange={handleChange} 
                icon={Hash} 
                placeholder="e.g. 45000" 
                required
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-4 pt-6">
            <button 
              type="button"
              onClick={onClose}
              className="flex-1 py-4 rounded-2xl bg-white/5 hover:bg-white/10 transition font-bold border border-white/10 text-white/70"
            >
              Cancel
            </button>
            <button 
              type="submit"
              disabled={loading}
              className="flex-[2] py-4 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-black shadow-xl shadow-blue-500/20 transition transform active:scale-95 disabled:opacity-50 disabled:scale-100 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Save size={20} /> Update Details
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const InputGroup = ({ label, name, type = "text", value, onChange, icon: Icon, placeholder, required = false }) => (
  <div className="space-y-2">
    <label className="text-[10px] uppercase font-bold text-white/30 ml-1">{label}</label>
    <div className="relative group">
      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-blue-400 transition-colors">
        <Icon size={18} />
      </div>
      <input 
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="w-full bg-white/5 border border-white/10 rounded-2xl pl-12 pr-4 py-3.5 text-sm focus:outline-none focus:border-blue-500/50 focus:bg-white/[0.08] transition-all text-white"
      />
    </div>
  </div>
);

export default EditPayrollInfoModal;
